/**
 * ERP Dashboard Tab Component
 * Quick actions, upcoming drills, and emergency type grid
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, Calendar, FileText, Siren,
  Phone, Mail, Users, AlertTriangle, CheckCircle, Clock,
  Shield, Flame, Heart, Plane, Building, Lock, AlertOctagon
} from 'lucide-react';
import { toast } from 'sonner';

// Emergency type config
export const EMERGENCY_TYPES = {
  aircraft_accident: { icon: Plane, color: 'red', label: 'Aircraft Accident' },
  aircraft_incident: { icon: AlertTriangle, color: 'orange', label: 'Aircraft Incident' },
  medical_emergency: { icon: Heart, color: 'rose', label: 'Medical Emergency' },
  fire: { icon: Flame, color: 'red', label: 'Fire' },
  security_threat: { icon: Shield, color: 'amber', label: 'Security Threat' },
  bomb_threat: { icon: AlertOctagon, color: 'red', label: 'Bomb Threat' },
  hijack: { icon: Lock, color: 'red', label: 'Hijack' },
  hazmat: { icon: AlertTriangle, color: 'yellow', label: 'Hazmat' },
  natural_disaster: { icon: AlertTriangle, color: 'blue', label: 'Natural Disaster' },
  infrastructure_failure: { icon: Building, color: 'gray', label: 'Infrastructure' },
  cyber_attack: { icon: Shield, color: 'purple', label: 'Cyber Attack' },
  other: { icon: AlertTriangle, color: 'gray', label: 'Other' }
};

export default function ERPDashboardTab({ 
  stats,
  drills, 
  checklists,
  onShowContactDialog,
  onShowDrillDialog,
  onShowActivateDialog,
  onNavigateToChecklists,
  onSelectChecklist
}) {
  return (
    <div className="space-y-4">
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase">Contacts</p>
                <p className="text-2xl font-bold text-white">{stats?.total_contacts || 0}</p>
              </div>
              <Users className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase">Checklists</p>
                <p className="text-2xl font-bold text-white">{stats?.total_checklists || 0}</p>
              </div>
              <FileText className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase">Drills</p>
                <p className="text-2xl font-bold text-white">{stats?.total_drills || 0}</p>
              </div>
              <Calendar className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase">Activations</p>
                <p className="text-2xl font-bold text-orange-400">{stats?.active_emergencies || 0}</p>
              </div>
              <Siren className="w-8 h-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Quick Actions */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={onShowContactDialog} className="justify-start">
              <Plus className="w-4 h-4 mr-2" /> Add Contact
            </Button>
            <Button variant="outline" onClick={onShowDrillDialog} className="justify-start">
              <Calendar className="w-4 h-4 mr-2" /> Schedule Drill
            </Button>
            <Button variant="outline" onClick={onNavigateToChecklists} className="justify-start">
              <FileText className="w-4 h-4 mr-2" /> View Checklists
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 justify-start" onClick={onShowActivateDialog}>
              <Siren className="w-4 h-4 mr-2" /> Activate Emergency
            </Button>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Upcoming Drills</CardTitle>
          </CardHeader>
          <CardContent>
            {drills.filter(d => d.status === 'scheduled').slice(0, 3).map(drill => {
              const config = EMERGENCY_TYPES[drill.emergency_type] || EMERGENCY_TYPES.other;
              return (
                <div key={drill.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/50 mb-2">
                  <div className="flex items-center gap-3">
                    <config.icon className={`w-5 h-5 text-${config.color}-400`} />
                    <div>
                      <p className="text-white font-medium">{drill.name}</p>
                      <p className="text-xs text-slate-400">{drill.scheduled_date}</p>
                    </div>
                  </div>
                  <Badge variant="outline">{drill.drill_type}</Badge>
                </div>
              );
            })}
            {drills.filter(d => d.status === 'scheduled').length === 0 && (
              <p className="text-slate-400 text-center py-4">No upcoming drills scheduled</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Emergency Types Grid */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Emergency Response Procedures</CardTitle>
          <CardDescription>Click to view response checklist</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Object.entries(EMERGENCY_TYPES).slice(0, 6).map(([key, config]) => {
              const checklist = checklists.find(c => c.emergency_type === key);
              return (
                <Card 
                  key={key}
                  className={`bg-${config.color}-500/10 border-${config.color}-500/30 hover:border-${config.color}-500/50 cursor-pointer transition-colors`}
                  onClick={() => {
                    if (checklist) {
                      onSelectChecklist(checklist);
                    } else {
                      toast.info('No checklist available for this type');
                    }
                  }}
                >
                  <CardContent className="p-4 text-center">
                    <config.icon className={`w-8 h-8 text-${config.color}-400 mx-auto mb-2`} />
                    <p className="text-sm font-medium text-white">{config.label}</p>
                    {checklist && (
                      <Badge className="mt-2 text-xs" variant="outline">
                        {checklist.items?.length || 0} steps
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
